/**
 * Lazy loaders and the small surface of hls.js / mpegts.js this package uses.
 * Loading on demand keeps both libraries out of the startup bundle.
 */
export interface HlsTrackLike {
  name?: string;
  lang?: string;
}

export interface HlsErrorData {
  type: string;
  details: string;
  fatal: boolean;
  response?: { code?: number };
}

export interface HlsLike {
  audioTracks: HlsTrackLike[];
  subtitleTracks: HlsTrackLike[];
  audioTrack: number;
  subtitleTrack: number;
  subtitleDisplay: boolean;
  /** Live: the position hls.js considers "live" (newest segments minus its target delay). */
  readonly liveSyncPosition: number | null;
  on(event: string, listener: (event: string, data: HlsErrorData) => void): void;
  loadSource(url: string): void;
  attachMedia(video: HTMLMediaElement): void;
  recoverMediaError(): void;
  destroy(): void;
}

/** The parts of hls.js' loader contract the URL-rewriting loader touches. */
export interface HlsLoaderContext {
  url: string;
}

export interface HlsLoaderCallbacks {
  onSuccess(response: { url: string }, stats: unknown, context: HlsLoaderContext, networkDetails: unknown): void;
  [name: string]: unknown;
}

export interface HlsLoaderClass {
  new (config: object): { load(context: HlsLoaderContext, config: object, callbacks: HlsLoaderCallbacks): void };
}

export interface HlsConstructor {
  new (config: object): HlsLike;
  DefaultConfig: { loader: HlsLoaderClass };
  isSupported(): boolean;
  Events: { MANIFEST_PARSED: string; ERROR: string; AUDIO_TRACKS_UPDATED: string; SUBTITLE_TRACKS_UPDATED: string };
  ErrorTypes: { NETWORK_ERROR: string; MEDIA_ERROR: string };
}

export interface MpegtsPlayerLike {
  on(event: string, listener: (type: string, detail: string, info?: { code?: number; msg?: string }) => void): void;
  attachMediaElement(video: HTMLMediaElement): void;
  detachMediaElement(): void;
  load(): void;
  unload(): void;
  pause(): void;
  destroy(): void;
}

export interface MpegtsLike {
  isSupported(): boolean;
  createPlayer(source: { type: string; isLive: boolean; url: string }, config: object): MpegtsPlayerLike;
  Events: { ERROR: string };
  ErrorTypes: { NETWORK_ERROR: string; MEDIA_ERROR: string };
  ErrorDetails: { NETWORK_STATUS_CODE_INVALID: string; MEDIA_CODEC_UNSUPPORTED: string; MEDIA_FORMAT_UNSUPPORTED: string; MEDIA_MSE_ERROR: string };
}

let hls: Promise<HlsConstructor> | null = null;
let mpegts: Promise<MpegtsLike> | null = null;

export function loadHls(): Promise<HlsConstructor> {
  hls ??= import('hls.js').then((module) => module.default as unknown as HlsConstructor);
  return hls;
}

export function loadMpegts(): Promise<MpegtsLike> {
  mpegts ??= import('mpegts.js').then((module) => (module.default ?? module) as unknown as MpegtsLike);
  return mpegts;
}
